'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { describeLoginError, login as loginRequest, type Credentials, type Session } from '@/services/authApi';
import { readStoredSession, writeStoredSession } from '@/services/sessionStorage';

interface AuthContextValue {
  session: Session | null;
  isAuthenticated: boolean;
  isSubmitting: boolean;
  errorMessage: string | null;
  signIn: (credentials: Credentials) => Promise<boolean>;
  signOut: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Session state for the operator login. Deliberately stored in
 * `sessionStorage`, not `localStorage`: these devices sit on shop counters and
 * shared back-office machines, so a closed tab should end the session.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // Start null on the server; hydrate from sessionStorage after mount.
  const [session, setSession] = useState<Session | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setSession(readStoredSession());
  }, []);

  const signIn = useCallback(async (credentials: Credentials) => {
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const nextSession = await loginRequest(credentials);
      setSession(nextSession);
      writeStoredSession(nextSession);
      return true;
    } catch (error) {
      setErrorMessage(describeLoginError(error));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const signOut = useCallback(() => {
    setSession(null);
    writeStoredSession(null);
  }, []);

  const clearError = useCallback(() => setErrorMessage(null), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      isAuthenticated: session !== null,
      isSubmitting,
      errorMessage,
      signIn,
      signOut,
      clearError,
    }),
    [session, isSubmitting, errorMessage, signIn, signOut, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}
